"use client"

import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  type Middleware,
  type OffsetOptions,
  type Placement,
  type ReferenceType,
  type UseFloatingReturn,
} from "@floating-ui/react"
import { useMemo, useState, type CSSProperties } from "react"

/**
 * Gap every v2 overlay keeps between itself and the viewport edges, in pixels.
 */
export const OVERLAY_VIEWPORT_PADDING = 8

/**
 * Gap between a trigger and the panel anchored to it, in pixels.
 */
export const OVERLAY_TRIGGER_GAP = 6

/** The space a panel has to render into, measured in pixels. */
export interface FloatingOverlaySize {
  maxWidth: number
  maxHeight: number
}

/** The custom property {@link matchReferenceWidth} publishes the trigger's width on. */
export const REFERENCE_WIDTH_VAR = "--v2-reference-width"

/**
 * Publishes the trigger's width to the panel as {@link REFERENCE_WIDTH_VAR},
 * so a panel can line up with a full-width control through a class such as
 * `min-w-[max(16rem,var(--v2-reference-width,0px))]` while keeping its own
 * floor for a narrow trigger.
 *
 * @returns A `size` middleware for {@link UseFloatingOverlayOptions.middleware}.
 */
export function matchReferenceWidth(): Middleware {
  return size({
    apply({ rects, elements }) {
      elements.floating.style.setProperty(REFERENCE_WIDTH_VAR, `${Math.ceil(rects.reference.width)}px`)
    },
  })
}

export interface UseFloatingOverlayOptions {
  /** Whether the panel is showing. Positioning only runs while it is. */
  open: boolean
  /** Called when floating-ui asks for the panel to close. */
  onOpenChange?: (open: boolean) => void
  /** Preferred placement. The panel flips or shifts away from it to fit. */
  placement?: Placement
  /**
   * Distance from the trigger, in pixels, or the full floating-ui offset
   * options when the alignment axis needs nudging too.
   */
  gap?: number | OffsetOptions
  /** Distance kept from the viewport edges, in pixels. */
  padding?: number
  /** Whether the panel may swap to the opposite side to fit. */
  flipToFit?: boolean
  /** Whether the measured width cap is reported in `overlayStyles`. */
  constrainWidth?: boolean
  /** Whether the measured height cap is reported in `overlayStyles`. */
  constrainHeight?: boolean
  /** Extra middleware, appended after the standard stack. */
  middleware?: Middleware[]
  /** Node identifier when the panel belongs to a floating tree. */
  nodeId?: string
  /** Positioning strategy. Portalled panels want the default, `fixed`. */
  strategy?: "absolute" | "fixed"
}

export interface UseFloatingOverlayReturn<RT extends ReferenceType = ReferenceType>
  extends UseFloatingReturn<RT> {
  /**
   * Position and size caps to spread onto the panel's `style`. Layer a
   * transition's styles after it so the animation wins on the properties it
   * owns.
   */
  overlayStyles: CSSProperties
  /** The space the panel has once the viewport padding is taken off. */
  available: FloatingOverlaySize | null
}

/**
 * Positions a floating panel so it stays inside the viewport, whatever the
 * trigger is up against.
 *
 * Every v2 overlay runs the same middleware stack through this hook rather than
 * assembling its own, so a popover, a menu and a tooltip all resolve a tight
 * corner the same way and no single component can quietly drift:
 *
 * - `offset` holds the gap to the trigger.
 * - `flip` swaps to the opposite side when the preferred one has no room.
 * - `shift` slides the panel along the edge it is running past, which is what
 *   turns a left-aligned panel on a right-edge trigger into a right-aligned
 *   one.
 * - `size` measures what is left and reports it as `maxWidth` and `maxHeight`,
 *   so a panel taller or wider than the space caps itself and scrolls inside
 *   instead of spilling off-screen.
 *
 * `autoUpdate` keeps all of that true while the page scrolls or resizes.
 */
export function useFloatingOverlay<RT extends ReferenceType = ReferenceType>(
  options: UseFloatingOverlayOptions,
): UseFloatingOverlayReturn<RT> {
  const {
    open,
    onOpenChange,
    placement = "bottom-start",
    gap = OVERLAY_TRIGGER_GAP,
    padding = OVERLAY_VIEWPORT_PADDING,
    flipToFit = true,
    constrainWidth = true,
    constrainHeight = true,
    middleware: extraMiddleware,
    nodeId,
    strategy = "fixed",
  } = options

  const [available, setAvailable] = useState<FloatingOverlaySize | null>(null)

  const floating = useFloating<RT>({
    nodeId,
    open,
    onOpenChange,
    placement,
    strategy,
    /**
     * Positioned with `top` and `left` rather than a transform, so a panel that
     * animates its own `scale` or `translate` cannot overwrite its position.
     */
    transform: false,
    middleware: [
      offset(gap),
      ...(flipToFit ? [flip({ padding })] : []),
      shift({ padding }),
      /**
       * Runs after `shift` on purpose: once shift has slid the panel to fit,
       * `size` reports the whole clipping width rather than the sliver left on
       * the trigger's own side.
       */
      size({
        padding,
        apply({ availableWidth, availableHeight }) {
          const next: FloatingOverlaySize = {
            maxWidth: Math.max(0, Math.floor(availableWidth)),
            maxHeight: Math.max(0, Math.floor(availableHeight)),
          }
          setAvailable((current) =>
            current &&
            current.maxWidth === next.maxWidth &&
            current.maxHeight === next.maxHeight
              ? current
              : next,
          )
        },
      }),
      ...(extraMiddleware ?? []),
    ],
    whileElementsMounted: autoUpdate,
  })

  const { floatingStyles } = floating

  const overlayStyles = useMemo<CSSProperties>(() => {
    const caps: CSSProperties = {}
    if (available) {
      if (constrainWidth) caps.maxWidth = available.maxWidth
      if (constrainHeight) caps.maxHeight = available.maxHeight
    }
    return { ...floatingStyles, ...caps }
  }, [floatingStyles, available, constrainWidth, constrainHeight])

  return { ...floating, overlayStyles, available }
}
